import { useMemo, useRef, useState } from "react";
import { useCallbackRef } from "use-callback-ref";
const isMouseOverElement = (elem, e) => {
    const { pageY, pageX } = e;
    const { left, right, bottom, top } = elem.getBoundingClientRect();
    return pageX > left && pageX < right && pageY > top && pageY < bottom;
};
/**
 * Use this hook to observe an HTML elements interaction state (hover, focus)
 *
 * _Note_: Observing is opt-in! Use the `observe` options the enable observing
 * for the different states.
 *
 * You can easily combine the `ElementInteractionState` with `classNames()` to
 * toggle classes base on the current state.
 *
 *     classNames(interactionState);
 */
export const useElementInteractionState = (options) => {
    const { observe } = options;
    /** Hover */
    const watchHover = observe.includes("hover");
    const [hover, setHover] = useState();
    // tracks the mouse position until the element is set, to detect the initial hover state
    const mousePosition = useRef(null);
    const handleMouseOver = () => {
        setHover(true);
    };
    const handleMouseOut = () => {
        setHover(false);
    };
    const handleMouseMouseMove = (e) => {
        mousePosition.current = { pageX: e.pageX, pageY: e.pageY };
    };
    /** Focus */
    const watchFocus = observe.includes("focus");
    const [focus, setFocus] = useState();
    const handleFocus = () => setFocus(true);
    const handleFocusOut = () => setFocus(false);
    const bindEarlyListeners = () => {
        if (watchHover) {
            document.addEventListener("mousemove", handleMouseMouseMove);
        }
        return;
    };
    const bindListeners = (element) => {
        if (watchHover) {
            // set initial hover
            if (hover === undefined) {
                document.removeEventListener("mousemove", handleMouseMouseMove);
                if (mousePosition.current) {
                    setHover(isMouseOverElement(element, mousePosition.current));
                }
            }
            element.addEventListener("mouseenter", handleMouseOver);
            element.addEventListener("mouseleave", handleMouseOut);
        }
        if (watchFocus) {
            // Set initial focus
            if (focus === undefined) {
                setFocus(document.activeElement === element);
            }
            element.addEventListener("focus", handleFocus);
            element.addEventListener("focusout", handleFocusOut);
        }
    };
    const unbindListeners = (element) => {
        if (watchHover) {
            element.removeEventListener("mouseenter", handleMouseOver);
            element.removeEventListener("mouseleave", handleMouseOut);
        }
        if (watchFocus) {
            element.removeEventListener("focus", handleFocus);
            element.removeEventListener("focusout", handleFocusOut);
        }
    };
    const ref = useCallbackRef(null, (element, prevElement) => {
        if (!prevElement && !element) {
            bindEarlyListeners();
        }
        if (!prevElement && element) {
            bindListeners(element);
        }
        if (prevElement && !element) {
            unbindListeners(prevElement);
        }
    });
    const state = useMemo(() => ({
        hover: !!hover,
        focus: !!focus,
    }), [hover, focus]);
    return [state, ref];
};
export default useElementInteractionState;
//# sourceMappingURL=data:application/json;base64,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